import React, {useState} from 'react'
import {Input} from '../../../../../accountManagement/shared/Input'
import {Button} from '../../../../../accountManagement/shared/Button'
import {useAuthClient} from '../../../../../utils/use-auth-client'

// Field component for type: 'validate-input'.
// Renders a text input with an inline "Validate" button that calls
// field.validateEndpoint to confirm the entered value is valid.
//
// Field config shape:
// {
//   key: string
//   label: string
//   placeholder?: string
//   validateEndpoint: string   — API route to call (TODO until endpoint is known)
//   validators?: string[]      — e.g. ['required']
//   helperText?: string
// }

export function ValidateInputField({
  field,
  value,
  variant,
  size,
  disabled,
  error,
  onChange,
  onBlur = () => {},
  onValidated = () => {},
}) {
  const client = useAuthClient()
  const [status, setStatus] = useState('idle') // 'idle' | 'loading' | 'valid' | 'invalid'

  const handleChange = e => {
    // Reset validation status when the user edits the value
    setStatus('idle')
    onChange(field.key, e.target.value)
  }

  const handleValidate = async () => {
    if (!value) return
    setStatus('loading')
    try {
      const url = field.validateAsPathParam
        ? `${field.validateEndpoint}/${encodeURIComponent(value)}`
        : field.validateEndpoint
      const res = field.validateAsPathParam
        ? await client(url)
        : await client(url, {data: {value}})

      let isValid
      if (field.responseQuantityField) {
        isValid = res?.[field.responseQuantityField] != null
      } else if (typeof field.validateResponse === 'function') {
        isValid = field.validateResponse(res)
      } else {
        isValid = Boolean(res)
      }

      setStatus(isValid ? 'valid' : 'invalid')
      onValidated(isValid, res)
    } catch {
      setStatus('invalid')
      onValidated(false, null)
    }
  }

  const errorText = status === 'invalid' ? 'Invalid value' : error
  const helperText = status === 'valid' ? 'Validated' : field.helperText

  return (
    <Input
      label={field.label}
      placeholder={field.placeholder}
      type="text"
      value={value}
      variant={variant}
      size={size}
      disabled={disabled}
      required={field.validators?.includes('required')}
      errorText={errorText}
      helperText={helperText}
      rightAction={
        <Button
          variant="secondary-field-blue"
          size="xl"
          type="button"
          isLoading={status === 'loading'}
          onClick={handleValidate}
        >
          Validate
        </Button>
      }
      onChange={handleChange}
      onBlur={() => onBlur(field.key)}
    />
  )
}
